class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    if (quantity === 0) {
      const currentItemElement = this.querySelector(`#CartDrawer-Item-${line}`) || this.querySelector(`[data-cart-item-index="${line}"]`);
      
      if (currentItemElement) {
        const parentKey = currentItemElement.getAttribute('data-cart-item-key') || currentItemElement.dataset.key;

        if (parentKey) {
          const childItemElement = this.querySelector(`[data-linked-to="${parentKey}"]`);

          if (childItemElement) {
            const childKey = childItemElement.getAttribute('data-cart-item-key') || childItemElement.dataset.key;
            
            if (childKey) {
              this.deleteMultipleItems({
                [parentKey]: 0,
                [childKey]: 0
              });
              return;
            }
          }
        }
      }
    }

    if (super.updateQuantity) {
      super.updateQuantity(line, quantity, name, variantId);
    }
  }

  async deleteMultipleItems(updates) {
    this.classList.add('is-loading');

    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      const responseSection = await fetch(`${window.location.pathname}?sections=cart-drawer,header`);
      const sectionData = await responseSection.json();

      const headerHtml = new DOMParser().parseFromString(sectionData['header'], 'text/html');
      const newHeaderCartIcon = headerHtml.getElementById('cart-icon-bubble');
      const currentHeaderCartIcon = document.getElementById('cart-icon-bubble');
      if (currentHeaderCartIcon && newHeaderCartIcon) {
        currentHeaderCartIcon.innerHTML = newHeaderCartIcon.innerHTML;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const drawerHtml = new DOMParser().parseFromString(sectionData['cart-drawer'], 'text/html');
      const newDrawerContent = drawerHtml.querySelector('cart-drawer');

      if (cartDrawer && newDrawerContent) {
        const currentInner = cartDrawer.querySelector('.drawer__inner');
        const newInner = newDrawerContent.querySelector('.drawer__inner');

        if (currentInner && newInner) {
          currentInner.innerHTML = newInner.innerHTML;
        } else {
          cartDrawer.innerHTML = newDrawerContent.innerHTML;
        }
        
        const isEmpty = newDrawerContent.querySelector('.drawer__inner').classList.contains('is-empty') || 
                        !newDrawerContent.querySelector('.cart-item');
        if (isEmpty) {
          cartDrawer.classList.add('is-empty');
          const emptyTemplate = newDrawerContent.querySelector('.drawer__inner-empty');
          if (emptyTemplate && cartDrawer.querySelector('.drawer__inner-empty')) {
            cartDrawer.querySelector('.drawer__inner-empty').innerHTML = emptyTemplate.innerHTML;
          }
        } else {
          cartDrawer.classList.remove('is-empty');
        }
      }

      if (window.pubsub && typeof window.pubsub.publish === 'function') {
        window.pubsub.publish(window.pubsub.events.cartUpdate, { source: 'cart-items' });
      }

    } catch (error) {
      console.error('Error deleting linked items:', error);
      window.location.reload();
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);